import type { Graph } from '@maxgraph/core';
import type { OperationNodeSpec } from './types';
import { generateId } from '../utils/id';
import { classify } from './classify';
import {
  CellNotFoundError,
  WrongCellTypeError,
  MissingTransferRowError,
  InvalidTransferRowError,
  ArchetypeFieldMismatchError,
  MissingComplexOptionsError,
} from './errors';
import type { CellStyle } from '@maxgraph/core';
import { COMPLEX_OP_DIMENSIONS, COMPLEX_OP_STYLE } from '../styles';

function validateArchetypeFields(spec: OperationNodeSpec): void {
  if (spec.archetype === 'TRANSFER') {
    if (!spec.transferRow) {
      throw new MissingTransferRowError(spec.id ?? '(new)');
    }
    const row = spec.transferRow;
    const hasQuantity = row.quantity !== undefined && row.quantity !== null;
    const hasSymbolic = row.symbolic !== undefined && row.symbolic !== null;
    if (hasQuantity && hasSymbolic) {
      throw new InvalidTransferRowError(
        'Transfer row cannot have both quantity and symbolic set.'
      );
    }
    if (!hasQuantity && !hasSymbolic) {
      throw new InvalidTransferRowError(
        'Transfer row must have either quantity or symbolic set.'
      );
    }
  }

  if (spec.archetype === 'COMPLEX') {
    if (!spec.complexOptions) {
      throw new MissingComplexOptionsError(spec.id ?? '(new)');
    }
  }
}

function buildOperationValue(spec: OperationNodeSpec) {
  return {
    kind: 'operation' as const,
    label: spec.displayName,
    id: spec.id,
    archetype: spec.archetype,
    displayName: spec.displayName,
    parameters: spec.parameters,
    operationTemplate: spec.operationTemplate,
    checkPoints: spec.checkPoints,
    duration: spec.duration,
    dataExpected: spec.dataExpected ?? false,
    groupId: spec.groupId ?? null,
    orderInGroup: spec.orderInGroup ?? null,
    transferRow: spec.transferRow,
    transferOptions: spec.transferOptions,
    uiGroupId: spec.uiGroupId,
    complexOptions: spec.complexOptions,
    notes: spec.notes,
    createdAt: spec.createdAt ?? new Date().toISOString(),
  };
}

export function addOperationNode(
  graph: Graph,
  spec: OperationNodeSpec,
): string {
  validateArchetypeFields(spec);

  const id = spec.id ?? generateId('operation');
  const value = buildOperationValue({ ...spec, id });
  const x = spec.position?.x ?? 0;
  const y = spec.position?.y ?? 0;
  const { width, height } = COMPLEX_OP_DIMENSIONS;

  graph.batchUpdate(() => {
    graph.insertVertex({
      id,
      value,
      position: [x, y],
      size: [width, height],
      style: COMPLEX_OP_STYLE,
    });
  });

  return id;
}

export function updateOperationNode(
  graph: Graph,
  id: string,
  patch: Partial<OperationNodeSpec>,
): void {
  const cell = graph.getDataModel().getCell(id);
  if (!cell) throw new CellNotFoundError(id);
  if (classify(cell) !== 'operation')
    throw new WrongCellTypeError(id, 'operation');

  const oldValue = cell.getValue();
  const merged = { ...oldValue, ...patch };

  // Re-validate if archetype changes
  if (patch.archetype) {
    // Clear stale archetype-specific fields
    if (patch.archetype !== 'TRANSFER') {
      merged.transferRow = undefined;
      merged.transferOptions = undefined;
    }
    if (patch.archetype !== 'COMPLEX') {
      merged.complexOptions = undefined;
    }
    validateArchetypeFields(merged as OperationNodeSpec);
  }

  // Re-validate transfer row on its own change
  if (patch.transferRow && merged.archetype === 'TRANSFER') {
    const specLike = { ...merged, archetype: 'TRANSFER' } as OperationNodeSpec;
    validateArchetypeFields(specLike);
  }

  // Update display label if displayName changed
  if (patch.displayName) {
    merged.label = patch.displayName;
  }

  graph.batchUpdate(() => {
    graph.getDataModel().setValue(cell, merged);

    // Update style if archetype changed
    if (patch.archetype) {
      const state = graph.getView().getState(cell);
      if (state) {
        graph.setCellStyle(
          { ...state.style, archetype: patch.archetype } as CellStyle,
          [cell],
        );
      }
    }
  });
}

export function removeOperationNode(graph: Graph, id: string): void {
  const cell = graph.getDataModel().getCell(id);
  if (!cell) throw new CellNotFoundError(id);
  if (classify(cell) !== 'operation')
    throw new WrongCellTypeError(id, 'operation');

  // If the operation belongs to a group, update the group's stepIds
  const val = cell.getValue();
  if (val?.groupId) {
    const groupCell = graph.getDataModel().getCell(val.groupId);
    if (groupCell) {
      const groupVal = groupCell.getValue();
      if (groupVal?.stepIds) {
        const updated = {
          ...groupVal,
          stepIds: groupVal.stepIds.filter((sid: string) => sid !== id),
        };
        graph.getDataModel().setValue(groupCell, updated);
      }
    }
  }

  graph.batchUpdate(() => {
    graph.removeCells([cell], true);
  });
}
